import React, {useCallback, useRef, useState} from 'react';
import classnames from 'classnames';

import {Button, Icon, IconButton, Popover} from '@albert-io/atomic';
import useAutoFocusReturn from 'lib/hooks/useAutoFocusReturn';
import useOnClickOutside from 'lib/hooks/useOnClickOutside';
import useOnEscape from 'lib/hooks/useOnEscape';
import useOnScroll from 'lib/hooks/useOnScroll';

import AnnotationNote from './AnnotationNote';
import {NOTE_TOOLTIP_PROPS} from './annotationConstants';
import {HIGHLIGHT_BACKGROUND_CLASS} from './annotationHighlightStyles';
import type {AnnotationType, HighlightColor} from './annotationTypes';

interface HighlightColorInfo {
  id: HighlightColor;
  label: string;
  className: string;
}

const HIGHLIGHT_COLORS: HighlightColorInfo[] = [
  {id: 'yellow', label: 'Yellow highlight', className: HIGHLIGHT_BACKGROUND_CLASS.yellow},
  {id: 'blue', label: 'Blue highlight', className: HIGHLIGHT_BACKGROUND_CLASS.blue},
  {id: 'pink', label: 'Pink highlight', className: HIGHLIGHT_BACKGROUND_CLASS.pink}
];

interface AnnotationPopoverProps {
  /** Viewport rect the popover hangs below: the finished selection, or the clicked point. */
  anchorRect?: DOMRect;
  /** A live box to hang from instead, tracked as it moves and resizes. */
  anchorEl?: HTMLElement | null;
  selectedColor: HighlightColor;
  selectedType: AnnotationType;
  note: string;
  onSelectColor: (color: HighlightColor) => void;
  onSelectUnderline: () => void;
  onNoteChange: (note: string) => void;
  onRemove: () => void;
  onDismiss: () => void;
  /** False where a mark cannot be an underline (a whole-supplement border). */
  allowUnderline?: boolean;
}

/** Editor for the active annotation: style picker (color highlight or underline) plus its note. */
export default function AnnotationPopover({
  anchorRect,
  anchorEl,
  selectedColor,
  selectedType,
  note,
  onSelectColor,
  onSelectUnderline,
  onNoteChange,
  onRemove,
  onDismiss,
  allowUnderline = true
}: AnnotationPopoverProps) {
  // State (not a ref) so Popover re-renders once the anchor span mounts.
  const [rectAnchorEl, setRectAnchorEl] = useState<HTMLSpanElement | null>(null);
  const target = anchorEl ?? rectAnchorEl;
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // preventScroll: focusing the swatch mustn't trip the scroll-dismiss below.
  const restoreFocus = useAutoFocusReturn(contentRef, Boolean(contentEl), true);
  // Popover portals content post-render; mirror to state so focus runs once it's in the DOM.
  const setContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      setContentEl(node);
      if (!node) restoreFocus();
    },
    [restoreFocus]
  );
  // capture: the freeze shield stops in-area mousedowns before they bubble to document.
  useOnClickOutside(contentRef, onDismiss, {capture: true});
  useOnEscape(contentRef, onDismiss);
  // A captured rect cannot follow a scroll; a tracked element can.
  const handleScroll = useCallback(
    (event: Event) => {
      const scrolled = event.target instanceof Node ? event.target : null;
      const scrolledOwnContent = Boolean(scrolled && contentRef.current?.contains(scrolled));
      const scrolledAnchorsScroller = Boolean(anchorEl && scrolled?.contains(anchorEl));

      if (scrolledOwnContent || scrolledAnchorsScroller) {
        return;
      }
      onDismiss();
    },
    [onDismiss, anchorEl]
  );
  useOnScroll(handleScroll);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Runs before the mode-level listener on document, which yields to a handled key.
    if (event.key === 'Escape') {
      // An Escape canceling an IME composition in the note must only cancel the
      // composition — stop it from reaching the document-level dismiss listener.
      if (event.nativeEvent.isComposing) {
        event.stopPropagation();
        return;
      }
      event.preventDefault();
      return;
    }
    // Portaled: Tab out of the swatches would strand focus, so close on the boundary Tab.
    if (event.key !== 'Tab') return;
    const swatches = contentRef.current?.querySelectorAll('button');
    if (!swatches?.length) return;
    const boundary = event.shiftKey ? swatches[0] : swatches[swatches.length - 1];
    if (document.activeElement === boundary) {
      event.preventDefault();
      onDismiss();
    }
  };

  return (
    <>
      {!anchorEl && anchorRect && (
        <span
          ref={setRectAnchorEl}
          aria-hidden
          className='pointer-events-none fixed'
          style={{
            left: anchorRect.left,
            top: anchorRect.top,
            width: anchorRect.width,
            height: anchorRect.height
          }}
        />
      )}
      {target && (
        // Padding on the content div so its clicks count as inside (click-outside).
        <Popover targetRef={{current: target}} expanded shadow='close' paddingLevel={0}>
          <div
            ref={setContentRef}
            onKeyDown={handleKeyDown}
            role='dialog'
            aria-label='Edit annotation'
            className='gap-x2 p-x2 flex flex-col'
          >
            <div className='gap-x1-half flex items-center'>
              <div className='gap-x1 flex' role='group' aria-label='Highlight color'>
                {HIGHLIGHT_COLORS.map(({id, label, className}) => {
                  const isSelected = selectedType === 'highlight' && id === selectedColor;
                  return (
                    <button
                      key={id}
                      type='button'
                      aria-label={label}
                      aria-pressed={isSelected}
                      onClick={() => onSelectColor(id)}
                      className={classnames(
                        'h-x4 w-x4 cursor-pointer rounded-full border-0',
                        className,
                        {'ring-4 ring-border-selected': isSelected}
                      )}
                    />
                  );
                })}
              </div>
              <div aria-hidden className='border-border-primary h-x2 border-l' />
              <IconButton
                icon='underline'
                iconStyle='regular'
                label='Underline'
                size='s'
                tooltip='Underline'
                tooltipProps={NOTE_TOOLTIP_PROPS}
                disabled={!allowUnderline}
                aria-pressed={selectedType === 'underline'}
                onClick={onSelectUnderline}
                className={classnames({
                  'ring-2 ring-border-selected': selectedType === 'underline'
                })}
              />
            </div>
            <div className='gap-x1 flex flex-col'>
              <AnnotationNote note={note} onNoteChange={onNoteChange} />
              <div className='gap-x2 flex items-center justify-between'>
                <Button
                  color='negative'
                  variant='text'
                  size='s'
                  onClick={onRemove}
                  className='gap-x1 flex items-center'
                >
                  <Icon icon='trash-alt' iconStyle='regular' />
                  Remove
                </Button>
                <Button color='primary' variant='solid' size='s' onClick={onDismiss}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        </Popover>
      )}
    </>
  );
}
